import React, { useState, useEffect } from 'react';

// Hooks
import { useDispatch, useSelector } from 'react-redux';

// Actions
import { fetchCustomFields, deleteCustomField, fetchCustomFieldTypes } from '~/redux/actions/customFields-actions';
import { success, failure } from '~/redux/actions/snackbar-actions';

// Utils
import { MESSAGES } from '~/utils/message';
import { DEFAULT_PAGINATION } from '~/utils/constants';

// Components
import CustomTable from './CustomTable';
import CreateModal from './CreateModal';
import EditCustomFieldModal from './EditCustomFieldModal';
import ConfirmModal from '~/components/General/ConfirmModal';
import NoRecords from '~/components/General/NoRecords';
import LimitReach from '~/components/General/LimitModal';
import OlcButton from '~/components/General/OlcButton';

// MUI Components
import { Box, Typography, Grid, GlobalStyles } from '@mui/material';

// Icons
import { GeneralInviteCIcon, CustomFieldIcon, ModalTrashIcon } from '~/assets/images';

// Styles
import './styles.scss';

const CustomFields = () => {
  const [createModal, setcreateModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [selectedCustomField, setSelectedCustomField] = useState({
    id: null,
    value: null,
    type: 'single_text',
  });
  const [customFieldTypes, setCustomFieldTypes] = useState([]);
  const [pagination, setPagination] = useState({ ...DEFAULT_PAGINATION, pageSize: 20 });
  const [limitModal, setLimitModal] = useState(false);
  const [limitMessage, setLimitMessage] = useState('');
  const [initialLoad, setInitialLoad] = useState(true);
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    message: '',
    header: '',
    icon: '',
    loading: false,
    handleOk: () => {},
    handleClose: () => {},
    successButtonName: '',
  });

  const dispatch = useDispatch();
  const rowsCount = useSelector((state) => state.customFieldsReducers.rowsCount);
  const isLoading = useSelector((state) => state.customFieldsReducers.loading);

  const deleteExistingCustomField = async (customFieldId) => {
    try {
      setConfirmModal((prev) => ({ ...prev, loading: true }));
      const response = await dispatch(deleteCustomField(customFieldId, pagination));
      dispatch(success(response?.data?.message));
      setConfirmModal((prev) => ({ ...prev, open: false, loading: false }));
    } catch (error) {
      dispatch(failure(error.response?.data?.message));
      setConfirmModal((prev) => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    let isActive = true;

    dispatch(fetchCustomFields(pagination)).finally(() => {
      if (isActive) {
        setInitialLoad(false);
      }
    });

    return () => {
      isActive = false;
    };
  }, [dispatch, pagination.page, pagination.pageSize]);

  useEffect(() => {
    const loadTypes = async () => {
      const response = await dispatch(fetchCustomFieldTypes());
      if (response?.status === 200) {
        setCustomFieldTypes(response.data.data || []);
      }
    };
    loadTypes();
  }, [dispatch]);

  return (
    <>
      <GlobalStyles styles={{ body: { backgroundColor: '#f9fafb' } }} />
      <Box className="olcWrapper olcSettingsListPage customFieldsPageWrapper">
        <Box className="olcScreenCard olcSettingsListCard customFieldsScreenCard">
          <Box className="olcHeaderWrap">
            <Box>
              <Typography className="pageTitle">{MESSAGES.SETTINGS.CUSTOM_FIELDS.TITLE}</Typography>
            </Box>
            <Box className="pageNewBtn">
              <OlcButton
                variant="primary"
                startIcon={<img src={GeneralInviteCIcon} alt="" />}
                onClick={() => setcreateModal(true)}
              >
                {MESSAGES.SETTINGS.CUSTOM_FIELDS.ADD_NEW}
              </OlcButton>
            </Box>
          </Box>
          {!initialLoad && !isLoading && (!rowsCount || rowsCount === 0) ? (
            <Box>
              <NoRecords
                icon={
                  <img
                    src={CustomFieldIcon}
                    style={{ height: '33px', width: '33px' }}
                    alt="no-records"
                  />
                }
                title={
                  MESSAGES?.SETTINGS?.CUSTOM_FIELDS?.NO_RECORDS_TITLE || 'No custom fields yet'
                }
                message={
                  <>
                    <p style={{ margin: '0px' }}>
                      Custom fields let you capture extra details on your records.
                    </p>
                    <p style={{ margin: '0px' }}>
                      Add your first custom field to stay better organized.
                    </p>
                  </>
                }
              />
            </Box>
          ) : (
            // <Grid container>
            //   <Grid item lg={12} md={12} sm={12} xs={12}>
            <CustomTable
              setEditModal={setEditModal}
              setSelectedCustomField={setSelectedCustomField}
              updatePagination={setPagination}
              pagination={pagination}
              initialLoad={initialLoad}
              customFieldTypes={customFieldTypes}
              onDelete={(customField) => {
                setConfirmModal({
                  open: true,
                  message: MESSAGES.SETTINGS.CUSTOM_FIELDS?.DELETE?.ARE_YOU_SURE,
                  header: MESSAGES.SETTINGS.CUSTOM_FIELDS?.DELETE?.DELETE_CUSTOM_FIELDS,
                  icon: ModalTrashIcon,
                  loading: false,
                  handleOk: () => deleteExistingCustomField(customField.id),
                  handleClose: () => setConfirmModal((prev) => ({ ...prev, open: false })),
                  successButtonName: MESSAGES.SETTINGS.CUSTOM_FIELDS.DELETE.SUBMIT_BUTTON,
                });
              }}
            />
            //   </Grid>
            // </Grid>
          )}
        </Box>
      </Box>
      <CreateModal
        openModal={createModal}
        handleCloseModal={() => setcreateModal(false)}
        pagination={pagination}
        updatePagination={setPagination}
        customFieldTypes={customFieldTypes}
        onLimitReached={(message) => {
          setcreateModal(false);
          setLimitMessage(message);
          setLimitModal(true);
        }}
      />
      <EditCustomFieldModal
        openModal={editModal}
        handleCloseModal={() => setEditModal(false)}
        selectedCustomField={selectedCustomField}
        pagination={pagination}
        customFieldTypes={customFieldTypes}
      />
      <LimitReach
        open={limitModal}
        onClose={() => {
          setLimitModal(false);
          setTimeout(() => setLimitMessage(''), 100);
        }}
        message={limitMessage}
        resourceType="custom fields"
      />
      <ConfirmModal openConfirmModal={confirmModal} />
    </>
  );
};

export default CustomFields;
